import React, { useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import HeightCoordinatorContext from './height-coordinator-context';

interface AnimatedFoldProps {
  isOpen: boolean;
  children: React.ReactNode;
}

const AnimatedFold: React.FC<AnimatedFoldProps> = ({ isOpen, children }) => {
  const { registerFold, requestHeightChange, syncHeight } = useContext(HeightCoordinatorContext);
  const id = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const unregisterRef = useRef<(() => void) | null>(null);
  const hasSettledOpenRef = useRef(false);
  const [renderChildren, setRenderChildren] = useState(isOpen);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  const setOuterRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (unregisterRef.current) {
        unregisterRef.current();
        unregisterRef.current = null;
      }
      if (el) {
        unregisterRef.current = registerFold(id, el, {
          onSettled: () => {
            hasSettledOpenRef.current = isOpenRef.current;
            if (!isOpenRef.current) setRenderChildren(false);
          }
        });
      }
    },
    [id, registerFold]
  );

  useEffect(() => {
    hasSettledOpenRef.current = false;
    if (isOpen) setRenderChildren(true);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      requestHeightChange(id, 0);
      return;
    }
    if (!renderChildren) return;
    const el = contentRef.current;
    if (!el) return;
    requestHeightChange(id, el.getBoundingClientRect().height);
  }, [id, isOpen, renderChildren, requestHeightChange]);

  useEffect(() => {
    if (!renderChildren) return;
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (!isOpenRef.current) return;
      // Nested disclosures should reflow an already-open step without replaying its fold animation.
      if (hasSettledOpenRef.current) {
        syncHeight(id, entry.contentRect.height);
        return;
      }
      requestHeightChange(id, entry.contentRect.height);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [id, renderChildren, requestHeightChange, syncHeight]);

  return (
    <div ref={setOuterRef} style={{ overflow: 'hidden' }}>
      <div ref={contentRef}>{renderChildren && children}</div>
    </div>
  );
};

export default AnimatedFold;
